import type { CSSProperties } from 'react';

import { Divider, Flex } from '@/components/base';
import Dropdown from '@/components/Dropdown/Dropdown';
import MenuItem from '@/components/Menu/MenuItem';

import { useIssueListActions } from '../../hooks/use-issue-list-query';
import type {
  CompositedSignalIssue,
  SignalDateSelectionInput,
} from '../../model';

export const IssueActionDropdown = (props: {
  selectedIssue: CompositedSignalIssue | null;
  dynamicFilterSet: StructuredFilter;
  datePreset: SignalDateSelectionInput;
  dropdownPosition?: CSSProperties;
  isDropdownOpen: boolean;
  closeDropdown: () => void;
  onToggleSeries?: (issueName: string) => void;
  onIsolateSeries?: (issueName: string) => void;
  isSeriesHidden?: boolean;
  isLastVisibleSeries?: boolean;
}) => {
  const {
    selectedIssue,
    dynamicFilterSet,
    datePreset,
    dropdownPosition,
    isDropdownOpen,
    closeDropdown: setIsDropdownOpen,
  } = props;
  const { navigateToIssueDetailPage, favoriteIssue, hideIssue } =
    useIssueListActions();

  const handleView = () => {
    if (!selectedIssue) return;
    navigateToIssueDetailPage(selectedIssue.name, dynamicFilterSet, datePreset);
    setIsDropdownOpen();
  };

  const handleFavorite = () => {
    if (!selectedIssue) return;
    favoriteIssue(selectedIssue.name, !selectedIssue.favorite);
    setIsDropdownOpen();
  };

  const handleHide = () => {
    if (!selectedIssue) return;
    hideIssue(selectedIssue.name, true);
    setIsDropdownOpen();
  };

  const handleToggleFromChart = () => {
    if (!selectedIssue) return;
    props.onToggleSeries?.(selectedIssue.name);
    setIsDropdownOpen();
  };

  const handleIsolate = () => {
    if (!selectedIssue) return;
    props.onIsolateSeries?.(selectedIssue.name);
    setIsDropdownOpen();
  };
  return (
    <Dropdown
      triggerPos={{
        top: dropdownPosition?.top as number,
        left: dropdownPosition?.left as number,
      }}
      status={isDropdownOpen}
      close={() => setIsDropdownOpen()}
      modalWidth="150px"
      modalOptions={{
        offset: {
          x: 8,
          y: 8,
        },
      }}
      style={{
        padding: 4,
      }}
    >
      {props.onToggleSeries && (
        <>
          <MenuItem
            id="toggle-series"
            node={props.isSeriesHidden ? 'Show on Chart' : 'Hide from Chart'}
            onClick={handleToggleFromChart}
            closeMenu={() => setIsDropdownOpen()}
            disabled={props.isLastVisibleSeries}
          />
          <MenuItem
            id="isolate-series"
            node="Show Only This"
            onClick={handleIsolate}
            closeMenu={() => setIsDropdownOpen()}
            disabled={props.isLastVisibleSeries}
          />
          <Flex padding="4px 8px">
            <Divider />
          </Flex>
        </>
      )}
      <MenuItem
        id="view"
        node="View"
        onClick={handleView}
        closeMenu={() => setIsDropdownOpen()}
      />
      <MenuItem
        id="favorite"
        node={selectedIssue?.favorite ? 'Unfavorite' : 'Favorite'}
        onClick={handleFavorite}
        closeMenu={() => setIsDropdownOpen()}
      />
      <MenuItem
        id="hide"
        node="Hide"
        onClick={handleHide}
        closeMenu={() => setIsDropdownOpen()}
      />
    </Dropdown>
  );
};
